import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuthStore } from '../../store/authStore';
import { loginRequest } from '../../config/authConfig';

interface ProtectedRouteProps {
  children: ReactNode;
}

const inProgressLabel: Record<string, string> = {
  [InteractionStatus.None]: 'None',
  [InteractionStatus.Startup]: 'Startup',
  [InteractionStatus.HandleRedirect]: 'HandleRedirect',
  [InteractionStatus.AcquireToken]: 'AcquireToken',
  [InteractionStatus.Logout]: 'Logout',
};

/**
 * Protected route wrapper component.
 * Waits for MSAL redirect handling (B2C callback) before redirecting.
 * When not authenticated, triggers B2C login redirect instead of a login page.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const { instance, inProgress, accounts } = useMsal();
  const loginStarted = useRef(false);

  const needsLogin =
    inProgress === InteractionStatus.None && accounts.length === 0 && !isAuthenticated;

  useEffect(() => {
    if (!needsLogin || loginStarted.current) return;
    loginStarted.current = true;
    if (import.meta.env.DEV) console.log('[ProtectedRoute] → redirect to B2C (not authenticated)');
    instance.loginRedirect(loginRequest).catch((err) => {
      console.error(err);
      loginStarted.current = false;
    });
  }, [needsLogin, instance]);

  const inProgressStr = inProgressLabel[inProgress] ?? String(inProgress);
  if (import.meta.env.DEV) {
    console.log('[ProtectedRoute] render', {
      path: location.pathname,
      inProgress: inProgressStr,
      accountsCount: accounts.length,
      isAuthenticated,
    });
  }

  const loadingSpinner = (
    <div className="flex justify-center items-center min-h-[40vh]">
      <div className="h-10 w-10 rounded-full border-4 border-[var(--glass-border)] border-t-[var(--mui-palette-primary-main)] animate-spin" />
    </div>
  );

  if (inProgress !== InteractionStatus.None) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] → show loading (MSAL inProgress !== None)');
    return loadingSpinner;
  }

  if (accounts.length > 0 && !isAuthenticated) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] → show loading (accounts present, auth store not set yet – waiting for SsoAuthHandler)');
    return loadingSpinner;
  }

  if (!isAuthenticated) {
    return loadingSpinner;
  }

  if (import.meta.env.DEV) console.log('[ProtectedRoute] → render children (allowed)');
  return <>{children}</>;
};
